// =========================================================
// Vojjin MOTION/TWEEN Utilities
// =========================================================
import {registerGlobal} from "./core.js";

class Motion {
    constructor(from, to, duration = 1000, options = {}) {
        this.from = typeof from === 'number' ? {value: from} : {...from};
        this.to = typeof to === 'number' ? {value: to} : {...to};
        this.duration = duration;

        this.easing = options.easing || "linear";
        this.onUpdate = options.onUpdate || (() => {
        });
        this.onComplete = options.onComplete || (() => {
        });
        this.onStart = options.onStart || (() => {
        });
        this.running = false;
        this.paused = false;
        this.startTime = null;
        this.elapsed = 0;
        this.raf = null;
    }

    start = () => {
        this.running = true;
        this.paused = false;
        this.startTime = performance.now();
        this.onStart(this.from);
        this._animate();
        return this;
    }

    _animate = (time) => {
        if (!this.running || this.paused) return;

        const now = performance.now();
        const progress = Math.min((now - this.startTime) / this.duration, 1);
        const eased = Easing.get(this.easing)(progress);

        const current = {};
        for (const key in this.from) {
            const start = this.from[key];
            const end = this.to[key];

            // detect color
            if (typeof start === 'string' && typeof end === 'string' && Motion._isColor(start) && Motion._isColor(end)) {
                const c1 = Motion._parseColor(start);
                const c2 = Motion._parseColor(end);
                const r = Math.round(c1.r + (c2.r - c1.r) * eased);
                const g = Math.round(c1.g + (c2.g - c1.g) * eased);
                const b = Math.round(c1.b + (c2.b - c1.b) * eased);
                const a = +(c1.a + (c2.a - c1.a) * eased).toFixed(3);
                current[key] = `rgba(${r},${g},${b},${a})`;
            } else if (typeof start === 'number' && typeof end === 'number') {
                current[key] = start + (end - start) * eased;
            } else {
                current[key] = end; // fallback
            }
        }
        this.onUpdate(current, progress);

        if (progress < 1) {
            this.raf = requestAnimationFrame(this._animate);
        } else {
            this.running = false;
            this.onComplete(this.to);
        }
    }

    static _isColor(value) {
        return typeof value === 'string' && /^(#|rgb|hsl)/i.test(value);
    }

    static _parseColor(value) {
        if (!value) return {r: 0, g: 0, b: 0, a: 1};

        // #RRGGBB or #RGB
        if (value.startsWith('#')) {
            let hex = value.slice(1);
            if (hex.length === 3)
                hex = hex.split('').map(c => c + c).join('');
            const num = parseInt(hex, 16);
            return {
                r: (num >> 16) & 255,
                g: (num >> 8) & 255,
                b: num & 255,
                a: 1
            };
        }

        // rgb/rgba
        const rgbMatch = value.match(/rgba?\(([^)]+)\)/i);
        if (rgbMatch) {
            const [r, g, b, a = 1] = rgbMatch[1].split(',').map(n => parseFloat(n.trim()));
            return {r, g, b, a};
        }

        // hsl/hsla
        const hslMatch = value.match(/hsla?\(([^)]+)\)/i);
        if (hslMatch) {
            const [h, s, l, a = 1] = hslMatch[1].split(',').map((v, i) =>
                i === 0 ? parseFloat(v) : parseFloat(v) / (i < 3 ? 100 : 1)
            );
            return Motion._hslToRgb(h, s, l, a);
        }

        // last resort — let browser parse
        const ctx = document.createElement('canvas').getContext('2d');
        ctx.fillStyle = value;
        const computed = ctx.fillStyle;
        const fallback = computed.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
        if (fallback) {
            return {
                r: +fallback[1],
                g: +fallback[2],
                b: +fallback[3],
                a: fallback[4] ? parseFloat(fallback[4]) : 1
            };
        }

        // totally failed
        return {r: 0, g: 0, b: 0, a: 1};
    }

// helper: HSL → RGB
    static _hslToRgb(h, s, l, a = 1) {
        h /= 360;
        const hue2rgb = (p, q, t) => {
            if (t < 0) t += 1;
            if (t > 1) t -= 1;
            if (t < 1 / 6) return p + (q - p) * 6 * t;
            if (t < 1 / 2) return q;
            if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
            return p;
        };
        let r, g, b;
        if (s === 0) {
            r = g = b = l;
        } else {
            const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
            const p = 2 * l - q;
            r = hue2rgb(p, q, h + 1 / 3);
            g = hue2rgb(p, q, h);
            b = hue2rgb(p, q, h - 1 / 3);
        }
        return {r: Math.round(r * 255), g: Math.round(g * 255), b: Math.round(b * 255), a};
    }

    pause = () => {
        if (!this.running || this.paused) return;
        this.paused = true;
        this.elapsed = performance.now() - this.startTime;
        cancelAnimationFrame(this.raf);
        return this;
    }

    resume = () => {
        if (!this.paused) return;
        this.paused = false;
        this.startTime = performance.now() - this.elapsed;
        this._animate();
        return this;
    }

    stop = () => {
        this.running = false;
        this.paused = false;
        cancelAnimationFrame(this.raf);
        return this;
    }
}

registerGlobal("Motion", Motion);